import { createPlugin } from "../createPlugin";
import { PlayerEvent } from "../../Core/PlayerEvents";
import { PlaybackPlatformEvent } from "../../Core/PlaybackPlatformEvents";

/**
 * Host analytics bridge — maps canonical Playback Event Bus events to a sink.
 * Provider-agnostic: new providers need no analytics changes.
 *
 * @param {object} [options]
 * @param {function} [options.sink] ({ type, payload, state }) => void
 */
export function createHostAnalyticsPlugin(options) {
  options = options || {};
  var sink = options.sink || function () {};
  var sessionId = options.sessionId || null;
  var sessionStartedAt = options.sessionStartedAt || null;
  var continuousSession = !!options.continuousSession;
  var maxPosition = 0;
  var lastEmit = Object.create(null);

  function emit(type, payload, ctx) {
    var state = ctx && ctx.getState ? ctx.getState() : {};
    var cfg = (ctx && ctx.config) || {};
    sink({
      type: type,
      payload: payload || null,
      state: state,
      session: {
        sessionId: sessionId || cfg.sessionId || null,
        startedAt:
          sessionStartedAt ||
          (cfg && cfg.sessionStartedAt) ||
          null,
        maxPosition: maxPosition,
        duration: state.duration || 0,
        completionPct:
          state.duration > 0
            ? Math.min(100, (maxPosition / state.duration) * 100)
            : 0,
      },
    });
  }

  function isPlay(eventName) {
    return (
      eventName === PlayerEvent.PLAYBACK_STARTED ||
      eventName === PlayerEvent.PLAY ||
      eventName === PlaybackPlatformEvent.PLAY
    );
  }

  function isPause(eventName) {
    return (
      eventName === PlayerEvent.PLAYBACK_PAUSED ||
      eventName === PlayerEvent.PAUSE ||
      eventName === PlaybackPlatformEvent.PAUSE
    );
  }

  return createPlugin({
    name: "host-analytics",
    version: "1.2.0",
    onReady: function (ctx) {
      if (!sessionStartedAt) {
        var cfg = ctx.config || {};
        sessionStartedAt = cfg.sessionStartedAt || Date.now();
      }
      emit("player_ready", null, ctx);
    },
    onEvent: function (eventName, payload, ctx) {
      var st = ctx.getState();
      if (st.currentTime > maxPosition) {
        maxPosition = st.currentTime;
      }

      if (eventName === "statechange") {
        if (st.playbackState === "playing" && !lastEmit.play) {
          lastEmit.play = true;
          lastEmit.pause = false;
          emit("play", payload, ctx);
        }
        if (st.playbackState === "paused" && lastEmit.play && !lastEmit.pause) {
          lastEmit.pause = true;
          lastEmit.play = false;
          emit("pause", payload, ctx);
        }
      }
      if (isPause(eventName)) {
        emit("pause", payload, ctx);
      }
      if (isPlay(eventName)) {
        emit("play", payload, ctx);
      }
      if (
        eventName === PlayerEvent.SEEK_COMPLETED ||
        eventName === PlayerEvent.SEEK_COMPLETE ||
        eventName === PlaybackPlatformEvent.SEEK_COMPLETE ||
        eventName === "seeked"
      ) {
        emit("seek", payload, ctx);
      }
      if (
        eventName === PlayerEvent.BUFFER_STARTED ||
        eventName === PlaybackPlatformEvent.BUFFERING
      ) {
        emit("buffer", payload, ctx);
      }
      if (
        eventName === PlayerEvent.PLAYBACK_ENDED ||
        eventName === PlaybackPlatformEvent.ENDED ||
        eventName === "ended"
      ) {
        emit("complete", payload, ctx);
      }
      if (
        eventName === "error" ||
        eventName === PlayerEvent.PLAYBACK_ERROR ||
        eventName === PlaybackPlatformEvent.ERROR
      ) {
        emit("error", payload, ctx);
      }
      if (
        eventName === PlayerEvent.TIME_UPDATE ||
        eventName === PlaybackPlatformEvent.PROGRESS ||
        eventName === "timeupdate"
      ) {
        var now = Date.now();
        if (!lastEmit.progressAt || now - lastEmit.progressAt > 5000) {
          lastEmit.progressAt = now;
          emit("progress", { currentTime: st.currentTime }, ctx);
        }
      }
    },
    onDestroy: function (ctx) {
      // Continuous UMXP sessions survive cinematic intro remount; Host emits
      // session_end on true page unmount.
      if (continuousSession) {
        return;
      }
      emit("session_end", null, ctx);
    },
  });
}

export default createHostAnalyticsPlugin;
